import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageSEO from "@/components/feature/PageSEO";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://chirunnexa.com";

const industrialOilsJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Shell Industrial Oil Series Wholesale | Tellus Omala Corena | Chirunnexa",
    "url": `${SITE_URL}/industrial-oils`,
    "description": "Full Shell industrial oil range wholesale: Tellus hydraulic oils, Omala gear oils, Corena compressor oils, Spirax transmission oils, Morlina bearing oils, Diala transformer oils. B2B export via China-Europe Railway and FCL/LCL.",
    "inLanguage": "en",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
        { "@type": "ListItem", "position": 2, "name": "Shell Industrial Oils", "item": `${SITE_URL}/industrial-oils` },
      ],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Shell Industrial Oil Series — B2B Wholesale",
    "description": "Complete Shell industrial lubricant range for B2B wholesale export from Guangzhou and Ningbo ports.",
    "url": `${SITE_URL}/industrial-oils`,
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Shell Tellus Hydraulic Oils (ISO VG 22–100)", "url": `${SITE_URL}/industrial-oils` },
      { "@type": "ListItem", "position": 2, "name": "Shell Omala Industrial Gear Oils (ISO VG 68–680)", "url": `${SITE_URL}/industrial-oils` },
      { "@type": "ListItem", "position": 3, "name": "Shell Corena Air Compressor Oils (ISO VG 32–100)", "url": `${SITE_URL}/industrial-oils` },
      { "@type": "ListItem", "position": 4, "name": "Shell Spirax Transmission & Axle Oils (GL-4/GL-5/ATF)", "url": `${SITE_URL}/industrial-oils` },
      { "@type": "ListItem", "position": 5, "name": "Shell Morlina Bearing & Circulating Oils (ISO VG 32–460)", "url": `${SITE_URL}/industrial-oils` },
      { "@type": "ListItem", "position": 6, "name": "Shell Diala Electrical Insulating Oils (IEC 60296)", "url": `${SITE_URL}/industrial-oils` },
    ],
  },
];

const industrialSeries = [
  {
    name: "Tellus 得力士",
    nameRu: "Теллус",
    subtitle: "Hydraulic Oils · Гидравлические масла",
    icon: "ri-settings-3-line",
    grades: "ISO VG 22 / 32 / 46 / 68 / 100",
    series: [
      { code: "S2M", desc: "Mineral-based, anti-wear, wide temperature range", descRu: "Минеральная основа, противоизносные, широкий диапазон температур" },
      { code: "S3M", desc: "Premium mineral, extended drain intervals, zinc-free option", descRu: "Премиальная минеральная, увеличенные интервалы замены" },
      { code: "S4ME", desc: "Full synthetic, energy-saving, ISO 46 flagship", descRu: "Полностью синтетическое, энергосберегающее, флагман ISO 46" },
    ],
    certs: "Denison HF-0/HF-1/HF-2 · Eaton/Vickers M-2950-S · Bosch Rexroth RE 90220 · Parker Hannifin",
    packs: ["20L", "208L Drum", "1000L IBC"],
    applications: "Hydraulic presses · Injection molding machines · CNC machining centers · Mining equipment",
    applicationsRu: "Гидравлические прессы · Литьевые машины · ЧПУ-обрабатывающие центры · Горнодобывающее оборудование",
  },
  {
    name: "Omala 可耐压",
    nameRu: "Омала",
    subtitle: "Industrial Gear Oils · Промышленные трансмиссионные масла",
    icon: "ri-loader-4-line",
    grades: "ISO VG 68 / 100 / 150 / 220 / 320 / 460 / 680",
    series: [
      { code: "S2G", desc: "Mineral EP gear oil, high load capacity, anti-scuff", descRu: "Минеральное EP, высокая нагрузочная способность" },
      { code: "S3GP", desc: "Premium mineral, polyglycol option, food-grade compatible", descRu: "Премиальная минеральная, совместима с пищевой промышленностью" },
      { code: "S4GX", desc: "Full synthetic PAO, extreme load, -40°C cold start", descRu: "Полностью синтетическое PAO, экстремальные нагрузки, запуск при -40°C" },
    ],
    certs: "Flender MD 050 · David Brown S1.53.101 · Wind Turbine OEMs · SEW · Siemens",
    packs: ["20L", "208L Drum", "1000L IBC"],
    applications: "Industrial gearboxes · Wind turbines · Steel mills · Paper mills · Mining conveyors",
    applicationsRu: "Промышленные редукторы · Ветрогенераторы · Сталелитейные заводы · Бумажные фабрики",
  },
  {
    name: "Corena 确能立",
    nameRu: "Корена",
    subtitle: "Air Compressor Oils · Масла для воздушных компрессоров",
    icon: "ri-tornado-line",
    grades: "ISO VG 32 / 46 / 68 / 100",
    series: [
      { code: "S2R", desc: "Mineral-based, rotary screw & vane compressors, 2000h drain", descRu: "Минеральная основа, ротационные компрессоры, 2000 ч замены" },
      { code: "S3R", desc: "Premium mineral, 4000h drain interval, deposit control", descRu: "Премиальная минеральная, 4000 ч, контроль отложений" },
      { code: "S4R", desc: "Full synthetic, 12,000h protection, energy saving 3.5%", descRu: "Полностью синтетическое, 12 000 ч защиты, экономия энергии 3,5%" },
    ],
    certs: "ABB VTR Certified · ISO 6743-3A-DAJ · Atlas Copco · Ingersoll Rand · Kaeser",
    packs: ["20L", "208L Drum"],
    applications: "Rotary screw compressors · Vane compressors · Reciprocating compressors · Centrifugal compressors",
    applicationsRu: "Ротационные винтовые компрессоры · Пластинчатые компрессоры · Поршневые компрессоры",
  },
  {
    name: "Spirax 施倍力",
    nameRu: "Спиракс",
    subtitle: "Transmission & Axle Oils · Трансмиссионные масла",
    icon: "ri-oil-line",
    grades: "GL-4 / GL-5 / ATF / ZF TE-ML",
    series: [
      { code: "S4G", desc: "Synthetic manual transmission, GL-4/GL-5, ZF TE-ML 02B/08", descRu: "Синтетическое для МКПП, GL-4/GL-5, ZF TE-ML 02B/08" },
      { code: "S6AXME", desc: "Synthetic axle oil, fuel economy, ZF TE-ML 05A/12B/16D/17B/19C/21A", descRu: "Синтетическое осевое, экономия топлива, ZF TE-ML 05A" },
      { code: "S6ATF ZM", desc: "Synthetic ATF, ZF 6HP/8HP, Mercedes 236.14/236.15, BMW ETL 7045E", descRu: "Синтетическое ATF, ZF 6HP/8HP, Mercedes 236.14/236.15" },
    ],
    certs: "ZF · Allison · Volvo · MAN · Scania · Mercedes-Benz · BMW · Caterpillar",
    packs: ["1L", "20L", "208L Drum"],
    applications: "Heavy trucks · Buses · Construction equipment · Agricultural machinery · Marine transmissions",
    applicationsRu: "Грузовые автомобили · Автобусы · Строительная техника · Сельхозтехника",
  },
  {
    name: "Morlina 万利得",
    nameRu: "Морлина",
    subtitle: "Bearing & Circulating Oils · Масла для подшипников",
    icon: "ri-temp-cold-line",
    grades: "ISO VG 32 / 46 / 100 / 220 / 320 / 460",
    series: [
      { code: "S2B", desc: "Mineral-based, anti-rust, anti-oxidant, bearing & circulating systems", descRu: "Минеральная основа, антикоррозионные, антиокислительные" },
      { code: "S4B", desc: "Full synthetic PAO, extreme conditions, -50°C to +150°C", descRu: "Полностью синтетическое PAO, экстремальные условия, -50°C до +150°C" },
    ],
    certs: "Alfa Laval · Cincinnati Milacron P-47 · David Brown S1.53.101 · Fives Cincinnati",
    packs: ["20L", "208L Drum", "1000L IBC"],
    applications: "Plain & rolling element bearings · Circulating systems · Textile machinery · Paper machines",
    applicationsRu: "Подшипники скольжения и качения · Циркуляционные системы · Текстильное оборудование",
  },
  {
    name: "Diala 大雅纳",
    nameRu: "Диала",
    subtitle: "Electrical Insulating Oils · Электроизоляционные масла",
    icon: "ri-flashlight-line",
    grades: "IEC 60296 · ASTM D3487 Type II",
    series: [
      { code: "S2ZU", desc: "Uninhibited transformer oil, IEC 60296 Class U, low pour point", descRu: "Ненингибированное трансформаторное масло, IEC 60296 класс U" },
      { code: "S3ZX", desc: "Inhibited transformer oil, IEC 60296 Class I, extended service life", descRu: "Ингибированное трансформаторное масло, IEC 60296 класс I" },
      { code: "S3ZX-IG", desc: "Inhibited, gas-absorbing, IEC 60296 Class II, high-voltage transformers", descRu: "Ингибированное, газопоглощающее, IEC 60296 класс II, высоковольтные трансформаторы" },
    ],
    certs: "IEC 62535 · ASTM D1275B · DIN 51353 · ABB · Siemens · Alstom",
    packs: ["208L Drum", "1000L IBC", "Bulk Tanker"],
    applications: "Power transformers · Distribution transformers · High-voltage switchgear · Capacitors",
    applicationsRu: "Силовые трансформаторы · Распределительные трансформаторы · Высоковольтное оборудование",
  },
];

const logisticsOptions = [
  { icon: "ri-ship-line", mode: "Ocean FCL/LCL", modeRu: "Морской FCL/LCL", desc: "Guangzhou Port & Ningbo Port", descRu: "Порты Гуанчжоу и Нинбо" },
  { icon: "ri-train-line", mode: "China-Europe Rail", modeRu: "Китайско-европейский ж/д", desc: "中欧班列 — CIS & Europe", descRu: "中欧班列 — СНГ и Европа" },
  { icon: "ri-truck-line", mode: "Overland Trucking", modeRu: "Автомобильный транспорт", desc: "EAEU last-mile delivery", descRu: "Доставка последней мили по ЕАЭС" },
];

export default function IndustrialOilsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"en" | "ru">("en");
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div style={{ background: "#F4F1EC", minHeight: "100vh" }}>
      <PageSEO
        title="Shell Industrial Oil Series Wholesale | Tellus Omala Corena | Chirunnexa"
        description="Full Shell industrial oil range wholesale from Guangzhou: Tellus hydraulic oils, Omala gear oils, Corena compressor oils, Spirax, Morlina, Diala. FCL/LCL and China-Europe Railway export. B2B quote within 24h."
        keywords="Shell industrial oils wholesale, Shell Tellus hydraulic oil, Shell Omala gear oil, Shell Corena compressor oil, industrial lubricants wholesale China, Shell Spirax transmission oil"
        canonical="/industrial-oils"
        hreflangRu="/ru/industrial-oils"
        jsonLd={industrialOilsJsonLd}
      />
      {/* Back nav */}
      <div
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 h-16"
        style={{ background: "rgba(244,241,236,0.97)", borderBottom: "1px solid rgba(0,0,0,0.07)", backdropFilter: "blur(16px)" }}
      >
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-xs font-medium tracking-widest cursor-pointer transition-colors duration-200 whitespace-nowrap"
          style={{ color: "rgba(30,45,65,0.55)", letterSpacing: "0.14em" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#D97706")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(30,45,65,0.55)")}
        >
          <i className="ri-arrow-left-line" />
          CHIRUNNEXA HUB
        </button>
        <div className="flex items-center gap-2">
          {(["en", "ru"] as const).map((l) => (
            <button
              key={l}
              onClick={() => setActiveTab(l)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all duration-200 whitespace-nowrap"
              style={{
                background: activeTab === l ? "rgba(245,158,11,0.12)" : "transparent",
                border: activeTab === l ? "1px solid rgba(245,158,11,0.4)" : "1px solid rgba(0,0,0,0.1)",
                color: activeTab === l ? "#D97706" : "rgba(30,45,65,0.5)",
              }}
            >
              {l === "en" ? "🌐 EN" : "🇷🇺 RU"}
            </button>
          ))}
        </div>
      </div>

      {/* Hero */}
      <div className="relative pt-16" style={{ minHeight: "55vh", background: "#0a0e1a" }}>
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/16588e47d7b7417ef33572c9a9f3e2c4.jpg"
            alt="Shell industrial 208L oil drum barrel wholesale B2B lubricants export"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(10,14,26,0.88) 0%, rgba(10,14,26,0.7) 50%, rgba(10,14,26,1) 100%)" }} />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-24">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8"
            style={{ background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.35)" }}
          >
            <i className="ri-settings-2-line text-xs" style={{ color: "#F59E0B" }} />
            <span className="text-xs tracking-widest uppercase font-medium" style={{ color: "#F59E0B", letterSpacing: "0.2em" }}>
              {activeTab === "en" ? "Strategic Commodity — Industrial Series" : "Стратегический товар — Промышленная серия"}
            </span>
          </div>
          <h1
            className="font-thin leading-none mb-4 text-white"
            style={{ fontSize: "clamp(2.4rem, 5vw, 4.5rem)", letterSpacing: "0.04em" }}
          >
            {activeTab === "en" ? (
              <>SHELL INDUSTRIAL<br /><span style={{ color: "#F59E0B" }}>OIL SERIES.</span></>
            ) : (
              <>ПРОМЫШЛЕННЫЕ МАСЛА<br /><span style={{ color: "#F59E0B" }}>SHELL.</span></>
            )}
          </h1>
          <p className="text-sm font-light max-w-2xl mt-4" style={{ color: "rgba(255,255,255,0.6)", lineHeight: "1.85" }}>
            {activeTab === "en"
              ? "Full Shell industrial range — Tellus, Omala, Corena, Spirax, Morlina, Diala. Strategic reserves for machinery and production lines. Volume fulfillment via China-Europe Railway Express (中欧班列) and FCL/LCL via Guangzhou & Ningbo Ports."
              : "Полная линейка промышленных масел Shell — Tellus, Omala, Corena, Spirax, Morlina, Diala. Стратегические запасы для машин и производственных линий. Оптовые поставки через 中欧班列 и FCL/LCL через порты Гуанчжоу и Нинбо."}
          </p>
        </div>
      </div>

      {/* Industrial Series Accordion */}
      <div className="max-w-6xl mx-auto px-6 py-20">
        <p className="text-xs tracking-widest uppercase font-semibold mb-10 text-center" style={{ color: "#D97706", letterSpacing: "0.22em" }}>
          {activeTab === "en" ? "Complete Industrial Oil Specifications" : "Полные технические характеристики промышленных масел"}
        </p>

        <div className="flex flex-col gap-4 mb-20">
          {industrialSeries.map((series) => (
            <div
              key={series.name}
              className="rounded-2xl overflow-hidden transition-all duration-300"
              style={{ background: "#ffffff", border: "1px solid rgba(0,0,0,0.07)" }}
            >
              <button
                className="w-full flex items-center gap-4 p-6 cursor-pointer text-left"
                onClick={() => setExpanded(expanded === series.name ? null : series.name)}
              >
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0"
                  style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.25)" }}
                >
                  <i className={`${series.icon} text-base`} style={{ color: "#D97706" }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-base font-bold" style={{ color: "#1a2332" }}>{series.name}</p>
                    <span className="text-xs font-light" style={{ color: "#D97706", fontStyle: "italic" }}>{series.nameRu}</span>
                  </div>
                  <p className="text-xs font-medium" style={{ color: "rgba(30,45,65,0.55)" }}>{series.subtitle}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span
                    className="text-xs font-semibold px-3 py-1 rounded-full hidden sm:block"
                    style={{ background: "rgba(245,158,11,0.1)", color: "#D97706", border: "1px solid rgba(245,158,11,0.25)" }}
                  >
                    {series.grades}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${expanded === series.name ? "rotate-180" : ""}`}
                    style={{ color: "#D97706" }}
                  />
                </div>
              </button>

              {expanded === series.name && (
                <div className="px-6 pb-6 border-t" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
                  <div className="pt-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs font-semibold mb-3 tracking-widest uppercase" style={{ color: "#D97706", letterSpacing: "0.15em" }}>
                        {activeTab === "en" ? "Product Series" : "Серии продуктов"}
                      </p>
                      <div className="flex flex-col gap-3">
                        {series.series.map((s) => (
                          <div key={s.code} className="flex gap-3 p-3 rounded-xl" style={{ background: "rgba(245,158,11,0.05)", border: "1px solid rgba(245,158,11,0.15)" }}>
                            <span className="text-xs font-bold shrink-0 mt-0.5" style={{ color: "#D97706", minWidth: "40px" }}>{s.code}</span>
                            <div>
                              <p className="text-xs font-light" style={{ color: "rgba(30,45,65,0.7)" }}>{s.desc}</p>
                              {activeTab === "ru" && (
                                <p className="text-xs font-light mt-1" style={{ color: "rgba(30,45,65,0.45)", fontStyle: "italic" }}>{s.descRu}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold mb-3 tracking-widest uppercase" style={{ color: "#D97706", letterSpacing: "0.15em" }}>
                        {activeTab === "en" ? "Certifications & OEM Approvals" : "Сертификаты и одобрения OEM"}
                      </p>
                      <p className="text-xs font-light mb-4 p-3 rounded-xl" style={{ color: "rgba(30,45,65,0.65)", background: "rgba(0,0,0,0.03)", lineHeight: "1.7" }}>
                        {series.certs}
                      </p>
                      <p className="text-xs font-semibold mb-2 tracking-widest uppercase" style={{ color: "#D97706", letterSpacing: "0.15em" }}>
                        {activeTab === "en" ? "Applications" : "Применение"}
                      </p>
                      <p className="text-xs font-light mb-4" style={{ color: "rgba(30,45,65,0.6)", lineHeight: "1.7" }}>
                        {activeTab === "en" ? series.applications : series.applicationsRu}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {series.packs.map((p) => (
                          <span key={p} className="px-3 py-1 rounded-full text-xs font-semibold" style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.25)", color: "#D97706" }}>
                            {p}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Logistics */}
        <div className="rounded-2xl p-8 mb-12" style={{ background: "#0a0e1a" }}>
          <p className="text-xs tracking-widest uppercase font-semibold mb-8 text-center" style={{ color: "#F59E0B", letterSpacing: "0.22em" }}>
            {activeTab === "en" ? "Export & Logistics Options" : "Варианты экспорта и логистики"}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {logisticsOptions.map((opt) => (
              <div key={opt.mode} className="flex flex-col items-center text-center p-6 rounded-xl" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <div className="w-12 h-12 flex items-center justify-center rounded-xl mb-4" style={{ background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.3)" }}>
                  <i className={`${opt.icon} text-xl`} style={{ color: "#F59E0B" }} />
                </div>
                <p className="text-sm font-bold text-white mb-1">{activeTab === "en" ? opt.mode : opt.modeRu}</p>
                <p className="text-xs font-light" style={{ color: "rgba(255,255,255,0.45)" }}>{activeTab === "en" ? opt.desc : opt.descRu}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <button
            onClick={() => navigate("/#contact")}
            className="px-10 py-4 rounded-xl text-sm font-bold tracking-widest cursor-pointer transition-all duration-200 hover:brightness-110 active:scale-95 text-white whitespace-nowrap"
            style={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)", letterSpacing: "0.14em" }}
          >
            {activeTab === "en" ? "REQUEST B2B VOLUME QUOTE" : "ЗАПРОСИТЬ ОПТОВОЕ ПРЕДЛОЖЕНИЕ"}
          </button>
          <p className="text-xs mt-3" style={{ color: "rgba(30,45,65,0.4)" }}>
            {activeTab === "en" ? "Legally binding contracts · Standard PO accepted · 24h response" : "Юридически обязывающие контракты · Принимаем PO · Ответ 24 ч"}
          </p>
        </div>
      </div>
    </div>
  );
}
